import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import Table from 'shell/components/shared/table';
import { generatePath, useNavigate } from 'react-router-dom';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesFeatureAuth } from '..';
import { useFormik } from 'formik';
import { FiltersFormFeatureAuth, FeatureAuthClient } from 'system/view-models/feature-auth-list';
import { transformDataset } from 'system/utils/feature-auth-transform-dataset';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import FiltersFeatureAuth from 'system/components/feature-auth/list/filter';
import { CommonFeatureAuthCrumbs } from 'system/constants/common-feature-auth-crumbs';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { headCells, validationSchemaGroupsFilter } from 'system/constants/feature-auth-list';
import ListHeader from 'shell/components/shared/list-header';
import { HttpResponseList } from 'shell/models/http-response';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { FeatureAuth } from 'shell/models/feature-auth';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { transformFeatureAuthList } from 'system/components/feature-auth/utils/transform-feature-auth-list';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { authsObj } from 'system/constants/feature-auth-authorizations';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { Typography } from '@mui/material';

const FeatureAuthList = (): React.ReactElement => {
  const [featureAuthList, getFeatureAuthList] = useHttpFetch<
    HttpResponseList<FeatureAuth>,
    Array<FeatureAuthClient>
  >(`${__MANAGEMENT_API_URL__}/tipo-feature-auth`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: [],
    },
    transformer: transformFeatureAuthList,
  });

  const deleteFeatureAuth = useHttpClient(`${__MANAGEMENT_API_URL__}/tipo-feature-auth`, {
    options: {
      errorCatching: true,
      method: 'delete',
      loading: true,
      data: {},
    },
  });

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<FeatureAuthClient>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<FeatureAuthClient>>({
    element: null,
    open: false,
  });

  const filterTable = useFilterTable<FiltersFormFeatureAuth>();

  const onSubmit = useCallback(
    (formValues: FiltersFormFeatureAuth) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterGroupForm.setFieldValue('page', 0);
      getFeatureAuthList({
        params: {},
        paramsSerializer: () => queryString,
      });
    },
    [getFeatureAuthList]
  );

  const filterGroupForm = useFormik({
    initialValues: initialValuesFeatureAuth,
    validationSchema: validationSchemaGroupsFilter,
    onSubmit,
  });

  useEffect(() => {
    filterGroupForm.setValues(filterTable.values || initialValuesFeatureAuth);
    const queryString = transformDataset(filterTable.values || initialValuesFeatureAuth);
    getFeatureAuthList({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterGroupForm.values);
  }, [filterGroupForm.values]);

  const count: number = useMemo(() => {
    return (featureAuthList.data as Array<FeatureAuthClient>)?.length || 0;
  }, [featureAuthList.data]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onVisibilityPress = useCallback(
    (cell): Promise<FeatureAuthClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/feature-auth/dettaglio/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<FeatureAuthClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/feature-auth/modifica/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<FeatureAuthClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<FeatureAuthClient>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      filterGroupForm.setFieldValue('page', newPage);
    },
    [filterGroupForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = { ...filterGroupForm.values, rowsPerPage: rowsPerPage || 10, page: 0 };
      filterGroupForm.setValues(newDataset);
    },
    [filterGroupForm.setFieldValue, filterGroupForm.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterGroupForm.values, orders };
      const queryString = transformDataset(newDataset);
      (
        getFeatureAuthList({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<FeatureAuth>, Array<FeatureAuthClient>>
      ).then(() => {
        filterGroupForm.setFieldValue('orders', orders);
      });
    },
    [filterGroupForm.setFieldValue, getFeatureAuthList, filterGroupForm.values]
  );

  return (
    <Fragment>
      <Breadcrumbs items={CommonFeatureAuthCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuova Autorizzazione"
        addBtnLabel="Aggiungi nuova Autorizzazione"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/feature-auth/nuovo');
        }}
        title="Gestione Autorizzazioni"
      />
      <div className="pmi-my-3">
        <FiltersFeatureAuth filtersFeatureAuthForm={filterGroupForm} />
      </div>
      {featureAuthList.data ? (
        (featureAuthList.data as Array<FeatureAuthClient>)?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterGroupForm.values.orders}
            activeActions={auths}
            page={filterGroupForm.values.page}
            rowsPerPage={filterGroupForm.values.rowsPerPage}
            body={featureAuthList.data as Array<FeatureAuthClient>}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Autorizzazioni"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Feature"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            deleteFeatureAuth({
              url: `/${deleteAlertModal.element?.code || ''}`,
            }) as ResponsePromise<void>
          ).then(() => {
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
            setDeleteAlertModal({ open: false, element: null });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Feature"
        message={`La cancellazione della feature ${deleteSuccessModal.element?.description} è avvenuta con successo.`}
        onClose={() => {
          const queryString = transformDataset({ ...filterGroupForm.values, page: 0 });
          getFeatureAuthList({
            params: {},
            paramsSerializer: () => queryString,
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
        onOk={() => {
          const queryString = transformDataset({ ...filterGroupForm.values, page: 0 });
          getFeatureAuthList({
            params: {},
            paramsSerializer: () => queryString,
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
      />
    </Fragment>
  );
};

FeatureAuthList.displayName = 'Feature Auth List component';

export default FeatureAuthList;
